/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getEventBus } from 'src/core/utils/eventBus';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    AddExampleSwaggerPayload,
    BatchAddExampleSwagger,
    batchAddExampleToUtteranceStores,
    PredictionSwagger,
    predictionSwaggerToPredictionStore,
    UtteranceSwagger,
    utteranceSwaggerToUtteranceStore
} from 'src/luis/projects/utterances/services/mappers';
import { getUtteranceService } from 'src/luis/projects/utterances/services/utteranceService';
import { PredictionStore } from 'src/luis/projects/utterances/stores/predictionStore';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { getUtteranceStores, isInvalidPredictions, toLabelsSwagger } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

export type UtteranceHandlerGroup = ReturnType<typeof createUtteranceHandlerGroup>;

export type ModelIdentifier = { model: Omit<Model, 'name'> };

/**
 * Creates a handler group of functions for the utterances to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createUtteranceHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const eventBus = getEventBus();
    const logger = getLogger();

    eventBus.on('notifyTrainingSucceeded', ({ appId }) => {
        const currentAppStore = rootStore.currentLuisAppStore;

        if (currentAppStore?.id === appId) {
            currentAppStore.currentAppVersionStore.predictionStoreHouse.invalidatePredictions();
        }
    });

    const getCurrentAppVersionStore = ({ appId, versionId }: VersionedAppId) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    /**
     * Loads a page of labels together with their predictions.
     *
     * @param param0.model Model id and typeId of the app to fetch the utterances from
     * @param param0.pageIndex Index of the page to be fetched.
     */
    const loadUtterancePage = async ({ pageIndex, model, context }: HandlerParams<ModelIdentifier & { pageIndex?: number }>) => {
        const { appId, versionId } = context;
        const utteranceService = getUtteranceService({ context });
        const { utteranceStoreHouse } = getUtteranceStores({
            model,
            currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
        });
        const logTimedActivity = logger.createTimedActivityLogger<readonly UtteranceSwagger[]>('GetLabels', () => ({ modelId: model.id }));

        const activePageIndex = pageIndex || utteranceStoreHouse.activePageIndex;

        if ((context.forceRefresh || !utteranceStoreHouse.getPage(activePageIndex)) && !utteranceStoreHouse.isLoading) {
            utteranceStoreHouse.setIsLoading(true);

            const utteranceSwaggers = await logTimedActivity(
                utteranceService.getLabels({
                    modelId: `${model.id}`,
                    pageInfo: { skip: utteranceStoreHouse.pageSize * activePageIndex, take: utteranceStoreHouse.pageSize }
                })
            );
            const utterances = utteranceSwaggers.map(utteranceSwaggerToUtteranceStore);

            const page: Map<LuisId, UtteranceStore> = utterances.reduce(
                (acc, val) => acc.set(val.id, val),
                new Map<LuisId, UtteranceStore>()
            );

            utteranceStoreHouse.setIsLoading(false);

            if (utteranceStoreHouse.isChunking) {
                // To avoid adding duplicate pages in case of using loadUtterancesByChunking while this handler still load.

                return;
            }

            utteranceStoreHouse.addPage(page, activePageIndex);
        }
    };

    /**
     * Initiates the app chunking process and loads the
     * fetched apps into the store.
     */
    const loadUtterancesByChunking = async ({ model, context }: HandlerParams<ModelIdentifier>) => {
        const { appId, versionId } = context;
        const { utteranceStoreHouse } = getUtteranceStores({
            model,
            currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
        });
        const { predictionStoreHouse } = getCurrentAppVersionStore({ appId, versionId });
        const utteranceService = getUtteranceService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<[readonly UtteranceSwagger[], readonly PredictionSwagger[]]>(
            'GetLabelsByChunking',
            () => ({ modelId: model.id })
        );

        if (utteranceStoreHouse.isChunking) {
            return;
        }

        utteranceStoreHouse.invalidate();
        utteranceStoreHouse.setIsChunking(true);

        let hitsCount = 0;
        const chunkSize = LuisConstants.ResourceLimits.ChunkingServiceLimit;
        const maxHitsCount = Math.ceil(utteranceStoreHouse.totalSize / LuisConstants.ResourceLimits.ChunkingServiceLimit);
        while (utteranceStoreHouse.isChunking) {
            if (hitsCount === maxHitsCount) {
                utteranceStoreHouse.setIsChunking(false);

                break;
            }

            const serviceParams = { modelId: model.id.toString(), pageInfo: { skip: hitsCount * chunkSize, take: chunkSize } };

            const [utteranceSwaggers, predictionSwaggers] = await logTimedActivity(
                Promise.all([utteranceService.getLabels(serviceParams), utteranceService.getPredictions(serviceParams)])
            );

            const utteranceStores = utteranceSwaggers.map(utteranceSwaggerToUtteranceStore);
            const predictionStores = predictionSwaggers.map((swagger, i) =>
                // The backend API for review predictions returns the utterance text null, however
                // it's keeping the same utterances order so we can use stores' text instead.
                predictionSwaggerToPredictionStore({ ...swagger, text: utteranceSwaggers[i].text })
            );

            utteranceStoreHouse.batchAddWithChunking(utteranceStores);
            predictionStoreHouse.addPredictions(predictionStores);
            hitsCount++;
        }
    };

    /**
     * Cancels any existing chunking operation.
     */
    const cancelUtterancesChunking = async ({ model, context }: HandlerParams<ModelIdentifier>) => {
        const { appId, versionId } = context;
        const { utteranceStoreHouse } = getUtteranceStores({
            model,
            currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
        });
        utteranceStoreHouse.setIsChunking(false);
    };

    /**
     * Loads or refreshes predictions of the specified text array.
     *
     * @param param0.appId App id of the app to fetch the utterances from
     * @param param0.versionId Version id of the app to fetch the utterances from
     * @param param0.utterances Text array to be predicted.
     */
    const loadPredictions = async ({ utterances, context }: HandlerParams<{ utterances: string[] }>) => {
        const { appId, versionId } = context;
        const utteranceService = getUtteranceService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<PredictionStore[]>('BatchPredict');

        const { predictionStoreHouse } = getCurrentAppVersionStore({ appId, versionId });

        if (isInvalidPredictions(utterances, predictionStoreHouse.predictions)) {
            predictionStoreHouse.setIsLoading(true);
            const id = getId();

            // LABELING_TODO
            getEventBus().emit('addToast', {
                id,
                title: 'Loading predictions...'
            });

            const predictionStores = await logTimedActivity(
                utteranceService.batchPredict({
                    textArr: utterances
                })
            );

            getEventBus().emit('updateToast', {
                id,
                toast: {
                    status: 'completed',
                    title: 'Predictions loaded'
                }
            });
            predictionStoreHouse.setIsLoading(false);
            predictionStoreHouse.addPredictions(predictionStores);
        }
    };

    /**
     * Adds an utterance to an intent.
     *
     * @param param0.model Model id and typeId of the app to fetch the utterances from
     * @param param0.text Utterance text to be added.
     * @param param0.labeledEntities Utterance labels to be added.
     */
    const addUtterances = async ({
        utterances,
        model,
        context,
        newUtterances
    }: HandlerParams<
        ModelIdentifier & { newUtterances: boolean; utterances: Pick<UtteranceStore, 'text' | 'tokenizedText' | 'labeledEntities'>[] }
    >) =>
        flow(async () => {
            const { appId, versionId } = context;
            const utteranceService = getUtteranceService({ context });
            const { intentStoreHouse, entityStoreHouse, predictionStoreHouse } = getCurrentAppVersionStore({ appId, versionId });
            const intent = intentStoreHouse.findById(model.id);
            const { utteranceStoreHouse } = getUtteranceStores({
                model,
                currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
            });
            let newUtteranceStores: UtteranceStore[];
            const enableSendingLabelsInNestedFormat = true;
            const logTimedActivity = logger.createTimedActivityLogger<BatchAddExampleSwagger>('AddUtterances', () => ({
                modelId: model.id
            }));

            let utterancesSwagger: BatchAddExampleSwagger;

            if (newUtterances) {
                const predictionStores = await utteranceService.batchPredict({ textArr: utterances.map(({ text }) => text) });

                predictionStoreHouse.addPredictions(predictionStores);

                const payload = utterances.map<AddExampleSwaggerPayload>(({ text }) => ({
                    text,
                    intentName: intent.name,
                    entityLabels: []
                }));

                utterancesSwagger = await logTimedActivity(utteranceService.addUtterances({ payload, enableSendingLabelsInNestedFormat }));

                const utteranceStores = batchAddExampleToUtteranceStores(
                    utterancesSwagger.filter(e => !e.hasError),
                    intent.id
                );

                newUtteranceStores = utteranceStores.map(
                    utteranceStore =>
                        new UtteranceStore(utteranceStore.id, {
                            ...utteranceStore,
                            labeledEntities: [],
                            tokenizedText: predictionStoreHouse.predictions.get(utteranceStore.text).tokenizedText
                        })
                );
            } else {
                const payload = utterances.map<AddExampleSwaggerPayload>(({ tokenizedText = [], labeledEntities = [], text }) => ({
                    text,
                    intentName: intent.name,
                    entityLabels: toLabelsSwagger({
                        entityStores: entityStoreHouse.items,
                        text,
                        tokenizedText,
                        labels: labeledEntities
                    })
                }));

                utterancesSwagger = await logTimedActivity(utteranceService.addUtterances({ payload, enableSendingLabelsInNestedFormat }));

                const utteranceStores = batchAddExampleToUtteranceStores(
                    utterancesSwagger.filter(e => !e.hasError),
                    intent.id
                );

                newUtteranceStores = utteranceStores.map(
                    utteranceStore =>
                        new UtteranceStore(utteranceStore.id, {
                            ...utteranceStore,
                            tokenizedText: utterances.find(u => u.text === utteranceStore.text).tokenizedText
                        })
                );
            }

            utteranceStoreHouse.batchAdd(newUtteranceStores);

            const failedUtterancesIds = utterancesSwagger.filter(u => u.hasError);

            if (failedUtterancesIds.length > 0) {
                // @TODO: We need to log & localize this error.
                throw new Error(`${failedUtterancesIds.length} utterances failed`);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes an array of utterances by their ids.
     *
     * @param param0.model Model id and typeId of the app to fetch the utterances from
     * @param param0.ids Utterance ids to be deleted.
     */
    const deleteUtterances = async ({ ids, model, context }: HandlerParams<ModelIdentifier & { ids: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const utteranceService = getUtteranceService({ context });
            const { utteranceStoreHouse } = getUtteranceStores({
                model,
                currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
            });

            for (const id of ids) {
                const logTimedActivity = logger.createTimedActivityLogger('DeleteUtterance', () => ({ modelId: model.id, id }));

                await logTimedActivity(utteranceService.deleteUtterance({ id }));
            }

            const pageInfo = utteranceStoreHouse.batchRemoveById(ids);

            if (pageInfo) {
                await loadUtterancePage({
                    pageIndex: pageInfo.skip / pageInfo.take,
                    model,
                    context: { ...context, forceRefresh: true }
                });
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Edits an utterance by deletion then addition
     * @param param0.id id of the utterance to be edited
     * @param param0.utterance the utterance info
     * @param param0.model the utterance associated model (entity/intent)
     * @param param0.intent in case the associated model is an entity
     */
    const editUtterance = async ({
        id,
        utterance,
        model,
        intent,
        context
    }: HandlerParams<
        ModelIdentifier & {
            id: LuisId;
            utterance: Pick<UtteranceStore, 'text' | 'tokenizedText' | 'labeledEntities'>;
            intent: Pick<Model, 'id' | 'description' | 'typeId'>;
        }
    >) => {
        await deleteUtterances({ ids: [id], model, context });
        await addUtterances({
            utterances: [utterance],
            model: intent,
            context,
            newUtterances: true
        });
    };

    /**
     * Deletes an array of utterances by their ids from the src intent and adds them to the dest intent.
     *
     * @param param0.appId App id of the app to delete the utterances from
     * @param param0.versionId Version id of the app to fetch the utterances from
     * @param param0.model Model id and typeId of the app to fetch the utterances from
     * @param param0.ids Utterance ids to be reassigned.
     * @param param0.srcIntentId Id of the source intent.
     * @param param0.destIntentId Id of the destination intent.
     */
    const reassignUtterances = async ({
        ids,
        srcIntentId,
        destIntentId,
        context
    }: HandlerParams<{ ids: LuisId[]; srcIntentId: LuisId; destIntentId: LuisId }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const { intentStoreHouse } = getCurrentAppVersionStore({ appId, versionId });
            const srcIntentStore = intentStoreHouse.findById(srcIntentId);
            const destIntentStore = intentStoreHouse.findById(destIntentId);
            const utteranceStores = ids.map(id => srcIntentStore.utteranceStoreHouse.findById(id));

            await addUtterances({
                model: { id: destIntentId, typeId: ModelType.Intent },
                utterances: utteranceStores,
                newUtterances: false,
                context
            });

            // Invalidates the other intent's cache.
            destIntentStore.utteranceStoreHouse.invalidate();
            const pageInfo = srcIntentStore.utteranceStoreHouse.batchRemoveById(ids);

            if (pageInfo) {
                await loadUtterancePage({
                    pageIndex: pageInfo.skip / pageInfo.take,
                    model: { id: srcIntentId, typeId: ModelType.Intent },
                    context: { ...context, forceRefresh: true }
                });
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Changes the utterance page size.
     *
     * @param param0.model Model id and typeId of the app to fetch the utterances from.
     * @param param0.newPageSize New page size.
     */
    const changeUtterancePageSize = async ({ newPageSize, model, context }: HandlerParams<ModelIdentifier & { newPageSize: number }>) => {
        const { appId, versionId } = context;
        const { utteranceStoreHouse } = getUtteranceStores({
            model,
            currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
        });

        const pageInfo = utteranceStoreHouse.changePageSize(newPageSize);

        if (pageInfo) {
            await loadUtterancePage({ model, pageIndex: pageInfo.skip / pageInfo.take, context: { ...context, forceRefresh: true } });
        }
    };

    /**
     * Changes the utterance page index.
     *
     * @param param0.model Model id and typeId of the app to fetch the utterances from.
     * @param param0.newPageSize New page index.
     */
    const changeUtterancePageIndex = async ({ pageIndex, model, context }: HandlerParams<ModelIdentifier & { pageIndex: number }>) => {
        const { appId, versionId } = context;
        const { utteranceStoreHouse } = getUtteranceStores({
            model,
            currentAppVersionStore: getCurrentAppVersionStore({ appId, versionId })
        });

        utteranceStoreHouse.changeActivePageIndex(pageIndex);
    };

    return {
        addUtterances,
        changeUtterancePageSize,
        deleteUtterances,
        editUtterance,
        loadUtterancePage,
        loadUtterancesByChunking,
        cancelUtterancesChunking,
        reassignUtterances,
        loadPredictions,
        changeUtterancePageIndex
    };
};
